// 환경설정 > 양식관리에 등록된 양식(레이아웃 JSON)을 그대로 그려, 연동 입력란(fieldKey)에만
// 값을 입력/표시하는 표. 견적서 작성폼처럼 "등록 양식 모양 그대로" 입력받아야 할 때 쓴다.
//   get(key)  : 입력란의 현재 값(없으면 '')
//   set(key,v): 값 변경(읽기전용이면 호출되지 않음)
//   locked(key): true면 자동 계산 칸(수정 불가)
//   renderField(key, cell, style): 특정 입력란을 직접 그리고 싶을 때(콤보박스 등) 요소를 돌려준다(null이면 기본 input)
//
// 2026-10: 서버가 만드는 등록양식 미리보기(backend/utils/approvalFormLayoutRender.js)와 "똑같이" 보이도록
// 글자크기(pt), 칸 여백(4px 6px), 기본 테두리(1px solid #000), 글꼴, 얇은 구분행, 로고 이미지/텍스트 상자
// 처리를 서버 렌더러와 동일한 규칙으로 맞췄다.
import { useEffect, useRef, useState } from 'react';
import API from '../api';

const pxBorder = (b) => {
  const st = String((b && b.style) || 'thin');
  let width = '1px', kind = 'solid';
  if (st === 'thick') width = '3px';
  else if (st === 'medium') width = '2px';
  else if (st === 'double') { width = '3px'; kind = 'double'; }
  else if (/dot|hair/i.test(st)) kind = 'dotted';
  else if (/dash/i.test(st)) kind = 'dashed';
  return `${width} ${kind} ${(b && b.color) || '#000'}`;
};

const BASE_FONT = "'나눔고딕', 'Malgun Gothic', sans-serif";

function TextBoxes({ boxes }) {
  return (boxes || []).map((tb, i) => (
    <div key={i} style={{ position: 'absolute', left: `${tb.left}%`, top: tb.top, width: `${tb.width}%`, minHeight: tb.height, zIndex: 3, overflow: 'visible', textAlign: 'left', fontWeight: 'normal' }}>
      {(tb.lines || []).map((l, j) => (
        <div key={j} style={{ fontSize: `${l.fontSize || 10}pt`, fontWeight: l.bold ? 'bold' : undefined, color: l.color || undefined, textAlign: l.align || 'left', whiteSpace: 'nowrap', lineHeight: 1.25, minHeight: '1em' }}>
          {l.text || ' '}
        </div>
      ))}
    </div>
  ));
}

// ── 서식수정 화면과 같은 "엑셀 원본 이미지" 위에 입력칸을 겹쳐 그리는 방식 ──────────────────────
// 서버(GET /quotations/linked-form/render)가 등록 양식(엑셀 원본)을 인쇄와 같은 경로로 그린 페이지 이미지와
// 칸 위치(페이지 대비 비율)를 돌려준다. 입력란(fieldKey)이 있는 칸 위치에 input을 올려 값을 입력/표시한다.
// 렌더가 불가능하면(엑셀 원본 없음 등) 아래 HtmlTable 방식으로 대신 그린다.
const _renderCache = {};
// 품목표에서 "마지막으로 작성된 줄 번호(N)"를 구한다. 번호/규격은 기본값이 들어 있을 수 있어 제외.
const BLANK_TEXT = '- 이 하 여 백 -';
function lastFilledItem(nums, get) {
  let N = 0;
  nums.forEach((n) => {
    const hit = ['model', 'group', 'qty', 'price'].some((c) => {
      const v = get(`item${n}_${c}`);
      return v !== undefined && v !== null && String(v) !== '';
    });
    if (hit) N = n;
  });
  return N;
}

function LinkedFormImage({ data, layout, get, set, locked, readOnly, renderField }) {
  const refs = useRef([]);
  const [widths, setWidths] = useState([]);
  const [activeAddr, setActiveAddr] = useState(null); // 입력 중인 칸(드롭다운이 다른 칸에 가리지 않도록 위로 올림)
  useEffect(() => {
    const ros = [];
    (data.pages || []).forEach((p, i) => {
      const el = refs.current[i];
      if (!el || typeof ResizeObserver === 'undefined') return;
      const ro = new ResizeObserver(() => setWidths(w => { const n = w.slice(); n[i] = el.clientWidth; return n; }));
      ro.observe(el); ros.push(ro);
    });
    return () => ros.forEach(r => r.disconnect());
  }, [data]);

  // 칸 주소 → 레이아웃 셀(글꼴/정렬 참고)
  const cellByAddr = {};
  (Array.isArray(layout?.pages) ? layout.pages : [{ rows: layout?.rows || [] }]).forEach(p => (p.rows || []).forEach(r => (r.cells || []).forEach(c => { if (c.address) cellByAddr[c.address] = c; })));

  // 서버가 입력란 목록(fields)을 비워 보내더라도, 칸 위치(cells)와 레이아웃의 입력란 지정으로 입력칸을 찾아 쓴다.
  const fieldList = (data.fields || []).some(f => f.key) ? data.fields
    : (data.cells || []).filter(c => cellByAddr[c.address] && cellByAddr[c.address].isField && cellByAddr[c.address].fieldKey)
        .map(c => ({ ...c, key: cellByAddr[c.address].fieldKey }));

  // 위치 계산이 잘못돼 입력 가능한 칸이 하나도 없으면(좌표 누락/비정상, 모두 잠김) 이미지 위 입력칸 방식은 쓸 수 없으니
  // 입력이 확실히 되는 표 방식으로 대신 그린다.
  const okRect = (f) => [f.x, f.y, f.w, f.h].every(n => typeof n === 'number' && isFinite(n)) && f.w > 0 && f.h > 0;
  const editableCount = fieldList.filter(f => f.key && okRect(f) && !(locked && locked(f.key))).length;
  const expectedCount = Object.values(cellByAddr).filter(c => c.isField && c.fieldKey && !(locked && locked(c.fieldKey))).length;
  if (!readOnly && (editableCount === 0 || (expectedCount > 0 && editableCount < expectedCount * 0.7))) return <LinkedFormHtmlTable layout={layout} get={get} set={set} locked={locked} readOnly={readOnly} renderField={renderField} />;

  // ── "- 이 하 여 백 -": 마지막 작성 품목 바로 아래 두 줄을 하나로 합쳐 표시 ──
  // 보기(읽기전용)에서는 작성된 줄 바로 다음 두 줄, 작성 중에는 입력용 한 줄을 남기고 그 다음 두 줄에 표시한다.
  const itemRows = {};
  fieldList.forEach((f) => {
    const m = /^item(\d+)_/.exec(f.key || '');
    if (!m || !okRect(f)) return;
    const n = Number(m[1]);
    const r = itemRows[n] || (itemRows[n] = { page: f.page, x1: f.x, x2: f.x + f.w, y1: f.y, y2: f.y + f.h, addr: f.address });
    r.x1 = Math.min(r.x1, f.x); r.x2 = Math.max(r.x2, f.x + f.w); r.y1 = Math.min(r.y1, f.y); r.y2 = Math.max(r.y2, f.y + f.h);
  });
  const itemNums = Object.keys(itemRows).map(Number).sort((a, b) => a - b);
  const filledN = lastFilledItem(itemNums, get);
  let marker = null;
  if (filledN > 0) {
    const a = filledN + (readOnly ? 1 : 2);
    const r1 = itemRows[a];
    if (r1) {
      const r2 = itemRows[a + 1] && itemRows[a + 1].page === r1.page ? itemRows[a + 1] : null;
      marker = { page: r1.page, x1: r1.x1, x2: r1.x2, y1: r1.y1, y2: (r2 || r1).y2, rows: r2 ? [a, a + 1] : [a], addr: r1.addr };
    }
  }
  const hiddenRows = new Set(marker ? marker.rows : []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, background: '#e5e7eb', padding: 12, borderRadius: 6 }}>
      {(data.pages || []).map((p, pi) => {
        const W = widths[pi] || 0;
        const pagePts = p.width / 1.5; // 이미지는 72dpi × 1.5배
        return (
          <div key={pi} ref={el => { refs.current[pi] = el; }}
            style={{ position: 'relative', width: '100%', maxWidth: Math.round(p.width / 1.5) + 200, boxShadow: '0 1px 6px rgba(0,0,0,.25)', background: '#fff' }}>
            <img src={p.image} alt={`${pi + 1}페이지`} draggable={false} style={{ width: '100%', display: 'block', userSelect: 'none' }} />
            {marker && marker.page === pi && (() => {
              const first = cellByAddr[marker.addr] || {};
              const fs = (first.fontSize || 10) * (W && pagePts ? W / pagePts : 1.333);
              return (
                <div style={{ position: 'absolute', zIndex: 6, pointerEvents: 'none', background: '#fff', boxSizing: 'border-box',
                  left: `calc(${marker.x1 * 100}% + 2px)`, top: `calc(${marker.y1 * 100}% + 2px)`,
                  width: `calc(${(marker.x2 - marker.x1) * 100}% - 4px)`, height: `calc(${(marker.y2 - marker.y1) * 100}% - 4px)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#000', fontSize: `${fs}px`, whiteSpace: 'nowrap' }}>
                  {BLANK_TEXT}
                </div>
              );
            })()}
            {fieldList.filter(f => f.page === pi && f.key && okRect(f) && !(marker && hiddenRows.has(Number((/^item(\d+)_/.exec(f.key) || [])[1])))).map(f => {
              const cell = cellByAddr[f.address] || {};
              const key = f.key;
              const val = get(key);
              const fs = (cell.fontSize || 10) * (W && pagePts ? W / pagePts : 1.333);
              const st = {
                width: '100%', height: '100%', border: 'none', outline: 'none', background: 'transparent', padding: '0 4px', margin: 0,
                boxSizing: 'border-box', textAlign: cell.align || 'left', fontWeight: cell.bold ? 'bold' : 'normal',
                fontSize: `${fs}px`, color: cell.textColor || '#000', fontFamily: 'inherit',
              };
              const box = { position: 'absolute', left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%`, display: 'flex', alignItems: 'center', boxSizing: 'border-box' };
              if (readOnly || (locked && locked(key))) {
                const shown = val === undefined || val === null ? '' : String(val);
                return shown === '' ? null : (
                  <div key={f.address} style={{ ...box, justifyContent: cell.align === 'right' ? 'flex-end' : cell.align === 'center' ? 'center' : 'flex-start', padding: '0 4px', fontSize: `${fs}px`, fontWeight: cell.bold ? 'bold' : 'normal', color: cell.textColor || '#000', whiteSpace: 'nowrap', overflow: 'hidden' }}>{shown}</div>
                );
              }
              const custom = renderField ? renderField(key, cell, st) : null;
              return (
                <div key={f.address} onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}
                  onFocus={() => setActiveAddr(f.address)} onBlur={() => setActiveAddr(a => (a === f.address ? null : a))}
                  style={{ ...box, zIndex: activeAddr === f.address ? 50 : 5, pointerEvents: 'auto', background: 'rgba(59,130,246,.06)', outline: '1px solid rgba(59,130,246,.35)' }}>
                  {custom || (
                    <input value={val === undefined || val === null ? '' : val}
                      type="text" autoComplete="off" spellCheck={false}
                      placeholder={key === 'doc_date' ? 'YYYY-MM-DD' : undefined}
                      onChange={e => set(key, e.target.value)} style={{ ...st, userSelect: 'text', cursor: 'text' }} />
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default function LinkedFormTable(props) {
  const [data, setData] = useState(_renderCache.data || null); // null=로딩, false=이미지 불가
  useEffect(() => {
    let alive = true;
    API.get(props.renderPath || '/quotations/linked-form/render')
      .then(r => { const d = r.data && r.data.found && r.data.pages && r.data.pages.length ? r.data : false; _renderCache.data = d || null; if (alive) setData(d); })
      .catch(() => { if (alive) setData(false); });
    return () => { alive = false; };
  }, [props.renderPath]);
  if (data === null) return <div style={{ padding: 30, textAlign: 'center', color: '#888', fontSize: 13 }}>서식을 불러오는 중...</div>;
  if (data) return <LinkedFormImage data={data} {...props} />;
  return <LinkedFormHtmlTable {...props} />;
}

function LinkedFormHtmlTable({ layout, get, set, locked, readOnly, renderField }) {
  const pages = Array.isArray(layout?.pages) && layout.pages.length ? layout.pages : [{ rows: layout?.rows || [] }];
  const colPct = layout?.colWidthsPct || [100];

  // "- 이 하 여 백 -": 품목 줄(item{n}_*)이 있는 행의 위치(행/열 범위)를 모으고, 마지막 작성 줄 아래 두 행을 하나로 합친다.
  const itemPos = {}; // n -> { pi, ri, from, to }
  pages.forEach((pg, pi) => (pg.rows || []).forEach((row, ri) => (row.cells || []).forEach((c, ci) => {
    const m = c.isField && !c.merged ? /^item(\d+)_/.exec(c.fieldKey || '') : null;
    if (!m) return;
    const n = Number(m[1]);
    const p = itemPos[n] || (itemPos[n] = { pi, ri, from: ci, to: ci });
    p.from = Math.min(p.from, ci); p.to = Math.max(p.to, ci);
  })));
  const itemNums = Object.keys(itemPos).map(Number).sort((a, b) => a - b);
  const filledN = lastFilledItem(itemNums, get);
  const mkA = filledN > 0 ? filledN + (readOnly ? 1 : 2) : 0;
  const mkP1 = mkA ? itemPos[mkA] : null;
  const mkP2 = mkP1 && itemPos[mkA + 1] && itemPos[mkA + 1].pi === mkP1.pi && itemPos[mkA + 1].ri === mkP1.ri + 1 ? itemPos[mkA + 1] : null;

  return (
    <div>
      {pages.map((page, pi) => (
        // 서버 미리보기와 같이 표 전체를 100% 폭 + 비율(%) 열 너비로 그린다.
        <div key={pi} style={{ overflowX: 'auto', marginBottom: pi < pages.length - 1 ? 16 : 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontFamily: BASE_FONT, color: '#000' }}>
            <colgroup>{colPct.map((w, i) => <col key={i} style={{ width: `${w}%` }} />)}</colgroup>
            <tbody>
              {(page.rows || []).map((row, ri) => (
                <tr key={ri} style={row.heightPx ? { height: row.heightPx } : undefined}>
                  {(row.cells || []).map((cell, ci) => {
                    if (cell.merged) return null;
                    if (mkP1 && mkP1.pi === pi) {
                      const first = mkP1.ri === ri, second = mkP2 && mkP2.ri === ri;
                      if (first || second) {
                        const p = first ? mkP1 : mkP2;
                        if (ci >= p.from && ci <= p.to) {
                          if (second || ci !== p.from) return null;
                          let span = 0;
                          for (let k = p.from; k <= p.to; k++) { const cc = row.cells[k]; if (cc && !cc.merged) span += cc.colSpan > 1 ? cc.colSpan : 1; }
                          return (
                            <td key={ci} colSpan={span > 1 ? span : undefined} rowSpan={mkP2 ? 2 : undefined}
                              style={{ border: '1px solid #000', padding: '4px 6px', textAlign: 'center', verticalAlign: 'middle',
                                fontWeight: 'bold', fontSize: `${cell.fontSize || 10}pt`, background: '#fff', color: '#000' }}>{BLANK_TEXT}</td>
                          );
                        }
                      }
                    }
                    const thin = row.heightPx && row.heightPx < 22;
                    const fs = cell.fontSize || 10;
                    const td = {
                      // 서버: td { border:1px solid #000; padding:4px 6px; vertical-align:middle; word-break }
                      border: '1px solid #000', padding: '4px 6px', verticalAlign: cell.valign || 'middle',
                      textAlign: cell.align || 'left', fontWeight: cell.bold ? 'bold' : 'normal',
                      fontSize: `${fs}pt`, wordBreak: 'break-word', overflowWrap: 'anywhere',
                      boxSizing: 'border-box',
                    };
                    if (cell.bgColor) td.backgroundColor = cell.bgColor;
                    if (cell.textColor) td.color = cell.textColor;
                    if (cell.italic) td.fontStyle = 'italic';
                    if (cell.underline) td.textDecoration = 'underline';
                    if (cell.fontFamily) td.fontFamily = `'${cell.fontFamily}', '나눔고딕', 'Malgun Gothic', sans-serif`;
                    if (cell.wrapText) td.whiteSpace = 'pre-wrap';
                    if (cell.borders) {
                      ['top', 'right', 'bottom', 'left'].forEach((side) => {
                        const b = cell.borders[side];
                        td[`border${side[0].toUpperCase()}${side.slice(1)}`] = b ? pxBorder(b) : 'none';
                      });
                    }
                    if (thin) {
                      const tfs = Math.max(1, Math.min(fs, Math.floor(row.heightPx * 0.75)));
                      Object.assign(td, { padding: 0, lineHeight: 1, fontSize: `${tfs}pt`, height: row.heightPx, overflow: 'hidden' });
                    }
                    if ((cell.image && cell.imageBox) || (cell.textBoxes && cell.textBoxes.length)) {
                      td.position = 'relative'; td.overflow = 'visible';
                    }

                    let content;
                    if (cell.image && cell.imageBox) {
                      const bx = cell.imageBox;
                      content = <img src={cell.image} alt="" style={{ position: 'absolute', left: `${bx.left}%`, top: bx.top, width: `${bx.width}%`, height: bx.height, maxWidth: 'none', zIndex: 2 }} />;
                    } else if (cell.image) {
                      content = <img src={cell.image} alt="" style={{ maxWidth: '100%', maxHeight: '100%', display: 'block', margin: '0 auto' }} />;
                    } else if (cell.isField && cell.fieldKey) {
                      const key = cell.fieldKey;
                      const val = get(key);
                      const fieldStyle = {
                        width: '100%', border: 'none', outline: 'none', background: 'transparent', padding: 0, margin: 0,
                        boxSizing: 'border-box', textAlign: cell.align || 'left', fontWeight: cell.bold ? 'bold' : 'normal',
                        fontSize: `${fs}pt`, color: cell.textColor || '#000', fontFamily: 'inherit',
                      };
                      if (readOnly || (locked && locked(key))) {
                        content = <span>{val === undefined || val === null || val === '' ? ' ' : String(val)}</span>;
                      } else {
                        const custom = renderField ? renderField(key, cell, fieldStyle) : null;
                        content = custom || (
                          <input value={val === undefined || val === null ? '' : val}
                            type="text" placeholder={key === 'doc_date' ? 'YYYY-MM-DD' : undefined}
                            onChange={e => set(key, e.target.value)} style={fieldStyle} />
                        );
                      }
                    } else {
                      content = cell.text ? <span style={{ whiteSpace: 'pre-wrap' }}>{cell.text}</span> : ' ';
                    }
                    return (
                      <td key={ci} rowSpan={cell.rowSpan > 1 ? cell.rowSpan : undefined}
                        colSpan={cell.colSpan > 1 ? cell.colSpan : undefined} style={td}>
                        {content}
                        {cell.textBoxes && cell.textBoxes.length ? <TextBoxes boxes={cell.textBoxes} /> : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
